import toast from "react-hot-toast";

import { IconsType } from "@web-app/components";
import config from "@web-app/config";
import {
  Order,
  OrderDeliveryType,
  OrderStatusProgress,
  Order as OrderType,
  Route,
  RoutePolylineType,
  WorkspaceConnection,
} from "@web-app/types";
import { dayjs } from "@web-app/utils/time";

// TODO: REMOVE THIS METHOD (this file should contain generic helpers)
export function searchOrders(
  searchText: string,
  list: Map<string, Order>,
): Map<string, Order> {
  if (searchText === "" || list.size === 0) {
    return new Map();
  }

  const searchTextRegex = new RegExp(`\\w*?${searchText}\\w*?\\s*?`);

  const searchProperty = (property: string) => {
    if (property) {
      return (
        searchTextRegex.test(property) ||
        searchTextRegex.test(property.toLowerCase())
      );
    }
    return false;
  };

  const filteredOrders = new Map<string, OrderType>();
  list.forEach((order, id) => {
    const pickupAddress = order?.pickup.address;
    const dropoffAddress = order?.dropoff.address;
    const orderDisplayId = order?.displayId;
    const recipientName = order?.dropoff.name;

    if (
      searchProperty(pickupAddress!) ||
      searchProperty(orderDisplayId) ||
      searchProperty(recipientName) ||
      searchProperty(dropoffAddress!)
    ) {
      filteredOrders.set(id, order);
    }
  });

  // Sort the filtered orders
  const sortedOrders = new Map(
    Array.from(filteredOrders.entries()).sort((a, b) => {
      const aOrder = a[1];
      const bOrder = b[1];
      const aDate = new Date(aOrder.scheduledStartDateTime);
      const bDate = new Date(bOrder.scheduledStartDateTime);

      return aDate.getTime() - bDate.getTime();
    }),
  );

  return sortedOrders;
}

export const OrderTimelineSchema: {
  [key: string]: {
    action: {
      [key: string]: {
        color: string;
        icon: IconsType;
        text: string;
      };
    };
    entityPresentation: EntityPresentationType | string;
  };
} = {
  actualPickupDateTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasCalendarDays",
        text: "Updated",
      },
    },
    entityPresentation: "pickup at",
  },
  assigneeId: {
    action: {
      DELETE: {
        color: "text-status-purple",
        icon: "fasLocationPin",
        text: "Unassigned order",
      },
      UPDATE: {
        color: "text-status-purple",
        icon: "fasLocationPin",
        text: "Assigned order to",
      },
    },
    entityPresentation: "Assignee",
  },

  attachments: {
    action: {
      CREATE: {
        color: "text-green-600",
        icon: "fasPhotoFilm",
        text: "Added",
      },
      DELETE: {
        color: "text-red-600",
        icon: "fasPhotoFilm",
        text: "Removed",
      },
      UPDATE: {
        color: "text-black",
        icon: "fasPhotoFilm",
        text: "Updated",
      },
    },
    entityPresentation: "Attachments",
  },

  autoAssign: {
    action: {
      ACCEPTED: {
        color: "text-green-600",
        icon: "fasLocationPin",
        text: "Auto-assign offer accepted by",
      },
      DECLINED: {
        color: "text-red-600",
        icon: "fasLocationPin",
        text: "Auto-assign offer declined by",
      },
      EXHAUSTED: {
        color: "text-red-600",
        icon: "fasLocationPin",
        text: "Auto-assign stopped — no available drivers",
      },
      EXPIRED: {
        color: "text-yellow-600",
        icon: "fasLocationPin",
        text: "Auto-assign offer expired for",
      },
      OFFERED: {
        color: "text-status-purple",
        icon: "fasLocationPin",
        text: "Auto-assign offered order to",
      },
    },
    entityPresentation: "Auto-assign",
  },

  connectedWorkspaceId: {
    action: {
      CREATE: {
        color: "text-black",
        icon: "fasGlobe",
        text: "Connected",
      },
      DELETE: {
        color: "text-red-600",
        icon: "fasGlobe",
        text: "Disconnected",
      },
      UPDATE: {
        color: "text-black",
        icon: "fasGlobe",
        text: "Connected",
      },
    },
    entityPresentation: "workspace",
  },

  customerRequirementNotes: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasNoteSticky",
        text: "Updated",
      },
    },
    entityPresentation: "customer requirement notes",
  },

  customerRequirementPhoto: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasPhotoFilm",
        text: "Updated",
      },
    },
    entityPresentation: "customer requirement photo",
  },

  customerRequirementSignature: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasSignature",
        text: "Updated",
      },
    },
    entityPresentation: "customer requirement signature",
  },

  description: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasStickyNote",
        text: "Updated",
      },
    },
    entityPresentation: "description",
  },

  externalId: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "external reference id",
  },

  geoDropoffDateTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasCalendarDays",
        text: "Updated",
      },
    },
    entityPresentation: "near destination at",
  },

  geoPickupDateTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasCalendarDays",
        text: "Updated",
      },
    },
    entityPresentation: "near pickup at",
  },
  height: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasPenField",
        text: "Updated",
      },
    },
    entityPresentation: "height",
  },

  itemsCount: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "itemsCount",
  },

  length: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasPenField",
        text: "Updated",
      },
    },
    entityPresentation: "length",
  },

  minVehicle: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasTruck",
        text: "Updated",
      },
    },
    entityPresentation: "minVehicle",
  },

  order: {
    action: {
      COPY: {
        color: "text-black",
        icon: "copy",
        text: "Copied order",
      },
      CREATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Created",
      },
      DELETE: {
        color: "text-red-600",
        icon: "fasLocationPin",
        text: "Deleted",
      },
    },
    entityPresentation: "order",
  },

  pickupAddress: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "pickup address",
  },

  pickupBusinessName: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasFont",
        text: "Updated",
      },
    },
    entityPresentation: "pickup business name",
  },

  pickupName: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasFont",
        text: "Updated",
      },
    },
    entityPresentation: "full name",
  },

  pickupNote: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasNoteSticky",
        text: "Updated",
      },
    },
    entityPresentation: "notes",
  },

  pickupPhone: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasMobile",
        text: "Changed",
      },
    },
    entityPresentation: "pickup phone number",
  },

  priority: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Changed",
      },
    },
    entityPresentation: "priority",
  },

  recipientAddress: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "drop-off location",
  },

  recipientBusinessName: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasFont",
        text: "Updated",
      },
    },
    entityPresentation: "recipient business name",
  },

  recipientName: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasFont",
        text: "Updated",
      },
    },
    entityPresentation: "recipient name",
  },

  recipientNote: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasNoteSticky",
        text: "Updated",
      },
    },
    entityPresentation: "recipient note",
  },

  recipientPhone: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasMobile",
        text: "Updated",
      },
    },
    entityPresentation: "recipient phone number",
  },

  scheduledEndDateTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasCalendarDays",
        text: "Updated",
      },
    },
    entityPresentation: {
      dropoff: "drop-off end time",
      individual: "drop-off at time",
      pickup: "pickup end time",
    },
  },

  scheduledStartDateTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasCalendarDays",
        text: "Updated",
      },
    },
    entityPresentation: {
      dropoff: "drop-off start time",
      individual: "pickup at time",
      pickup: "pickup start time",
    },
  },

  serviceTime: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "service time",
  },

  status: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocationPin",
        text: "Updated",
      },
    },
    entityPresentation: "status",
  },

  statusNotes: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasNoteSticky",
        text: "Updated",
      },
    },
    entityPresentation: "status notes",
  },

  statusReason: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasMessage",
        text: "Updated",
      },
    },
    entityPresentation: "status reason",
  },

  tip: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasHandHoldingDollar",
        text: "Updated",
      },
    },
    entityPresentation: "tip",
  },
  value: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasLocation",
        text: "Updated",
      },
    },
    entityPresentation: "value",
  },
  weight: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasPenField",
        text: "Updated",
      },
    },
    entityPresentation: "weight",
  },
  width: {
    action: {
      UPDATE: {
        color: "text-black",
        icon: "fasPenField",
        text: "Updated",
      },
    },
    entityPresentation: "width",
  },
};

// E.164 format: +[country code][number]
// Country code: 1-3 digits
// Number: 1-15 digits
// Total length: max 16 characters (including +)
export const validPhoneNumberRegex: RegExp = /^\+[1-9]\d{1,3}\d{4,14}$/;

export const validatePhoneNumber = (phone: string, showError?: boolean) => {
  if (phone && validPhoneNumberRegex.test(phone) && phone.length <= 15) {
    return true;
  } else {
    if (showError) {
      toast.error("Please enter a valid phone number (e.g. +1234567890)");
    }
    return false;
  }
};

export function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === "undefined" ? undefined : JSON.parse(value ?? "");
  } catch {
    console.error("parsing error on", { value });
    return undefined;
  }
}

export const getOrderCompletionTime = (order: Order) => {
  return order.type === OrderDeliveryType.ROUTE_OPT_PICKUP
    ? order.actualPickupDateTime
    : order.actualEndDateTime;
};

export const isActiveOrderDelayed = (
  order: Order | undefined,
  draftEtaDate?: Date | string | null,
) => {
  if (!order) {
    return false;
  }

  const completionDate = getOrderCompletionTime(order);
  const scheduledDate = order?.scheduledEndDateTime;
  const etaDate = draftEtaDate ?? order?.estimatedEtaDateTime;

  return Boolean(
    !completionDate &&
    scheduledDate &&
    etaDate &&
    new Date(etaDate) > new Date(scheduledDate),
  );
};

export const isEarlyDeliveredOrder = (
  order: Order | undefined,
  draftEtaDate?: Date | string | null,
) => {
  if (!order) {
    return false;
  }

  const scheduledStartTime = order.scheduledStartDateTime;
  const isOrderAssignedOrInprogress = [
    "TODO",
    "ASSIGNED",
    "INPROGRESS",
  ].includes(order.status);

  // for P2P order
  // if status === "INPROGRESS" and statusprogress === "DROPOFF",
  // use eta else use estimatedEtaDateTime
  if (order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
    const isInprogressDropoff =
      order.status === "INPROGRESS" &&
      order.statusProgress === OrderStatusProgress.DROPOFF;
    const etaDate = isInprogressDropoff
      ? order.eta
        ? order.eta.dateTime
        : order.estimatedEtaDateTime
      : order.estimatedEtaDateTime;

    const isEarlyDeliveredP2POrder =
      scheduledStartTime &&
      etaDate &&
      new Date(etaDate) < new Date(scheduledStartTime);

    return Boolean(isEarlyDeliveredP2POrder && isOrderAssignedOrInprogress);
  } else {
    // for Routed orders
    // order is early delivered and status is assigned or inprogress or todo
    const etaDate = draftEtaDate ? draftEtaDate : order.estimatedEtaDateTime;
    const isEarlyDeliveredRoutedOrder = Boolean(
      scheduledStartTime &&
      etaDate &&
      new Date(etaDate) < new Date(scheduledStartTime),
    );

    return Boolean(isEarlyDeliveredRoutedOrder && isOrderAssignedOrInprogress);
  }
};

export const getStatusPropertyLabelFromList = (
  value: string,
  options: { label: string; value: string }[],
  progress?: OrderStatusProgress | null,
): string => {
  let label = "";
  if (value === "INPROGRESS" && progress) {
    label =
      options.find((option) => option.value === `${value}_${progress}`)
        ?.label || "";
  } else {
    label = options.find((option) => option.value === value)?.label || "";
  }

  return label;
};

export const getPropertyLabelFromList = (
  value: string,
  options: { label: string; value: string }[],
): string => {
  return options.find((option) => option.value === value)?.label || "";
};

export type EntityPresentationType = {
  dropoff: string;
  individual: string;
  pickup: string;
};

export const getPolyLineDistance = (polyline: RoutePolylineType) => {
  const distanceInMeters = polyline.routes?.[0].distanceMeters;
  const distanceInMiles = distanceInMeters
    ? getMetersToMiles(distanceInMeters)
    : null;
  return distanceInMiles;
};

export const getMetersToMiles = (meters: number) => {
  return (meters * 0.000621371192).toFixed(1);
};

export const getRouteScheduledDates = (
  route: Route,
  orders: Map<string, Order>,
) => {
  const firstOrderId = route.ordersOrder[0].id;
  const firstOrder = orders.get(firstOrderId);

  const lastOrderId = route.ordersOrder[route.ordersOrder.length - 1].id;
  const lastOrder = orders.get(lastOrderId);

  return {
    endDate: lastOrder?.scheduledEndDateTime ?? new Date(),
    startDate: firstOrder?.scheduledStartDateTime ?? new Date(),
  };
};

export const getTimezoneForRoute = (
  orders: Order[] | undefined,
): keyof typeof timeZoneMap => {
  const timezone: keyof typeof timeZoneMap = "system";
  const pickupOrder = orders?.find(
    (order) => order.type === OrderDeliveryType.ROUTE_OPT_PICKUP,
  );
  const dropoffOrder = orders?.find(
    (order) => order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF,
  );
  return pickupOrder?.timezone || dropoffOrder?.timezone || timezone;
};

export const todaysStartDate = new Date();
todaysStartDate.setHours(0, 0, 0, 0);
export const todaysStartDateString = todaysStartDate.toISOString();
export const todaysEndDate = new Date();
todaysEndDate.setHours(23, 59, 59, 999);
export const todaysEndDateString = todaysEndDate.toISOString();

// Map timezone abbreviations to IANA timezone names
export const TIMEZONE_MAP: Record<string, string> = {
  CST: "America/Chicago",
  EST: "America/New_York",
  GMT: "Europe/London",
  MST: "America/Denver",
  PST: "America/Los_Angeles",
};

// Timezone-aware date helpers
export const createTodaysDateInTimezone = (
  userTimezone: string | undefined,
) => {
  if (!userTimezone || userTimezone === "system") {
    // Use browser's local timezone - keep existing behavior
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    return {
      endDateString: endDate.toISOString(),
      startDateString: startDate.toISOString(),
    };
  }

  const timezoneToUse = TIMEZONE_MAP[userTimezone] || userTimezone;

  // Create today's date at midnight in user's timezone
  const startDate = dayjs().tz(timezoneToUse).startOf("day").toDate();
  const endDate = dayjs().tz(timezoneToUse).endOf("day").toDate();

  return {
    endDateString: endDate.toISOString(),
    startDateString: startDate.toISOString(),
  };
};

export const pluralize = (word: string, count: number) => {
  return count === 1 ? word : `${word}s`;
};

export const timeZoneMap = {
  "Africa/Abidjan": "GMT",

  "Africa/Accra": "GMT",

  "Africa/Algiers": "CET",

  "Africa/Cairo": "EET",

  "Africa/Casablanca": "WET",

  "Africa/Dakar": "GMT",

  // Indian Standard Time
  "Africa/Johannesburg": "SAST",

  "Africa/Khartoum": "EAT",

  "Africa/Lagos": "WAT",

  "Africa/Nairobi": "EAT",

  "Africa/Tripoli": "EET",

  "Africa/Tunis": "CET",

  // Mountain Standard Time (no DST)
  "America/Anchorage": "AKDT",

  "America/Anguilla": "AST",

  "America/Antigua": "AST",

  "America/Argentina/Buenos_Aires": "ART",

  "America/Aruba": "AST",

  "America/Asuncion": "PYT",

  "America/Bahamas": "EST",

  "America/Barbados": "AST",

  "America/Belize": "CST",

  "America/Bermuda": "ADT",

  // Central Standard Time (Mexico)
  "America/Bogota": "COT",

  "America/Buenos_Aires": "ART",

  "America/Caracas": "VET",

  "America/Cayman": "EST",

  "America/Chicago": "CST",

  "America/Costa_Rica": "CST",

  "America/Curacao": "AST",

  "America/Denver": "MST",

  "America/Dominica": "AST",

  "America/Edmonton": "MDT",

  "America/El_Salvador": "CST",

  "America/Grenada": "AST",

  "America/Guatemala": "CST",

  "America/Halifax": "ADT",

  "America/Havana": "CDT",

  "America/Honduras": "CST",

  "America/Jamaica": "EST",

  "America/La_Paz": "BOT",

  // Colombia Time
  "America/Lima": "PET",

  "America/Los_Angeles": "PST",

  // West Africa Time
  "America/Mexico_City": "CST",

  "America/Montevideo": "UYT",

  // Western European Time
  "America/Montreal": "EST",

  "America/Montserrat": "AST",

  "America/New_York": "EST",

  "America/Nicaragua": "CST",

  "America/Panama": "EST",

  "America/Phoenix": "MST",

  "America/Puerto_Rico": "AST",

  // China Standard Time (Taiwan)
  "America/Santiago": "CLST",

  "America/Santo_Domingo": "AST",

  "America/Sao_Paulo": "BRT",

  "America/St_Johns": "NDT",

  "America/St_Kitts": "AST",

  "America/St_Lucia": "AST",

  "America/St_Vincent": "AST",

  // Korea Standard Time
  "America/Toronto": "EST",

  "America/Tortola": "AST",

  "America/Turks_and_Caicos": "EST",

  "America/Vancouver": "PST",

  "America/Virgin": "AST",

  "Antarctica/Casey": "AWST",

  "Antarctica/Davis": "DAVT",

  "Antarctica/DumontDUrville": "DDUT",

  "Antarctica/Macquarie": "AEDT",

  "Antarctica/Mawson": "MAWT",

  "Antarctica/Palmer": "CLST",

  "Antarctica/Rothera": "ROTT",

  "Antarctica/Syowa": "SYOT",

  "Antarctica/Troll": "CET",

  "Antarctica/Vostok": "VOST",

  // Arabia Standard Time
  "Asia/Amman": "EET",

  // Arabia Standard Time
  "Asia/Bahrain": "AST",

  "Asia/Bangkok": "ICT",

  "Asia/Beirut": "EET",

  "Asia/Damascus": "EET",

  "Asia/Dhaka": "BST",

  "Asia/Dubai": "GST",

  "Asia/Hong_Kong": "HKT",

  // Indochina Time
  "Asia/Jakarta": "WIB",

  "Asia/Jerusalem": "IDT",

  // China Standard Time
  "Asia/Kolkata": "IST",

  // Peru Time
  "Asia/Kuala_Lumpur": "MYT",

  // Israel Daylight Time
  "Asia/Kuwait": "AST",

  // Malaysia Time
  "Asia/Manila": "PHT",

  // Arabia Standard Time
  "Asia/Qatar": "AST",

  // Arabia Standard Time
  "Asia/Riyadh": "AST",

  // Gulf Standard Time
  "Asia/Seoul": "KST",

  "Asia/Shanghai": "CST",

  "Asia/Singapore": "SGT",

  "Asia/Taipei": "CST",

  "Asia/Tokyo": "JST",

  // Western Indonesian Time
  "Australia/Melbourne": "AEDT",

  "Australia/Sydney": "AEDT",

  // East Africa Time
  "Canada/Central": "CST",

  "Canada/Eastern": "EST",

  "Canada/Mountain": "MST",

  "Canada/Pacific": "PST",

  "Etc/GMT": "GMT",

  "Etc/UTC": "UTC",

  "Europe/Amsterdam": "CET",

  "Europe/Athens": "EET",

  "Europe/Berlin": "CET",

  "Europe/Bucharest": "EET",

  "Europe/Budapest": "CET",

  "Europe/Copenhagen": "CET",

  "Europe/Dublin": "GMT",

  // Philippine Time
  "Europe/Helsinki": "EET",

  "Europe/Istanbul": "TRT",

  "Europe/Lisbon": "WET",

  "Europe/London": "GMT",

  "Europe/Madrid": "CET",

  "Europe/Moscow": "MSK",

  "Europe/Oslo": "CET",

  "Europe/Paris": "CET",

  "Europe/Prague": "CET",

  "Europe/Riga": "EET",

  "Europe/Rome": "CET",

  "Europe/Sofia": "EET",

  "Europe/Stockholm": "CET",

  "Europe/Tallinn": "EET",

  "Europe/Vienna": "CET",

  "Europe/Vilnius": "EET",

  "Europe/Warsaw": "CET",

  "Europe/Zurich": "CET",

  "Indian/Comoro": "EAT",

  "Indian/Kerguelen": "TFT",

  "Indian/Maldives": "MVT",

  "Indian/Mauritius": "MUT",

  "Indian/Mayotte": "EAT",

  "Indian/Reunion": "RET",

  "Indian/Seychelles": "SCT",

  "Pacific/Auckland": "NZDT",

  "Pacific/Bougainville": "BST",

  "Pacific/Chatham": "CHAST",

  // Solomon Islands Time
  "Pacific/Efate": "VUT",

  "Pacific/Fakaofo": "TKT",

  // Vanuatu Time
  "Pacific/Fiji": "FJT",

  "Pacific/Funafuti": "TVT",

  // Bougainville Standard Time (Solomon Islands) - Note the potential for overlap with Bangladesh
  "Pacific/Guadalcanal": "SBT",

  "Pacific/Guam": "ChST",

  // Bangladesh Standard Time
  "Pacific/Honolulu": "HST",

  "Pacific/Majuro": "MHT",

  "Pacific/Nauru": "NRT",

  "Pacific/Niue": "NUT",

  "Pacific/Noumea": "NCT",

  "Pacific/Pago_Pago": "SST",

  "Pacific/Port_Moresby": "PGT",

  "Pacific/Rarotonga": "CKT",

  "Pacific/Saipan": "ChST",

  "Pacific/Tahiti": "TAHT",

  "Pacific/Tarawa": "GILT",

  "Pacific/Tongatapu": "TOT",

  "Pacific/Wake": "WAKT",
  "Pacific/Wallis": "WFT",
  system: "system",
};

export const generateTrackingLink = (
  wsCustomDomain: string,
  routeId: string,
) => {
  return `https://${wsCustomDomain}.${config.CUSTOM_DOMAIN_SUFFIX}/t/r/${routeId}`;
};

export const generateOrderTrackingLink = (
  wsCustomDomain: string,
  orderId: string,
) => {
  return `https://${wsCustomDomain}.${config.CUSTOM_DOMAIN_SUFFIX}/t/o/${orderId}`;
};

export const getProperWsCustomDomainAndId = (
  wsConnections: WorkspaceConnection[],
  orderWsId: string,
  orderConnWsId: string | null,
  id: string,
  isCurrentWsOrder: boolean,
  currentWorkspaceCustomDomain: string,
) => {
  if (wsConnections.length === 0 && isCurrentWsOrder) {
    if (!currentWorkspaceCustomDomain) return ["", ""];

    return [currentWorkspaceCustomDomain, id];
  }

  const orderActualWsConn = wsConnections?.find((connection) => {
    if (orderConnWsId) {
      if (isCurrentWsOrder) {
        return connection.workspaceId === orderWsId;
      } else {
        return connection.connectedWorkspaceId === orderWsId;
      }
    } else {
      return connection.workspaceId === orderWsId;
    }
  });

  if (!orderActualWsConn) return ["", ""];

  const orderWsCustomDomain = orderConnWsId
    ? isCurrentWsOrder
      ? orderActualWsConn?.workspace.customDomain
      : orderActualWsConn?.connectedWorkspace.customDomain
    : orderActualWsConn?.workspace.customDomain;

  if (!orderWsCustomDomain) return ["", ""];

  return [orderWsCustomDomain, id];
};
